import {AdyenPaymentsService} from '../../services/payments'
import {AdyenPaymentsDetailsService} from '../../services/payments-details'
import {executeCallbacks, executeErrorCallbacks} from '../../utils/executeCallbacks'
import {getCurrencyValueForApi} from '../../utils/parsers.mjs'
import {PaymentCancelService} from '../../services/payment-cancel'

export const baseConfig = (props) => {
    const {
        beforeSubmit = [],
        afterSubmit = [],
        beforeAdditionalDetails = [],
        afterAdditionalDetails = [],
        onError = [],
    } = props
    // Create error handler with props in closure
    const errorHandler = (error, component) => onErrorHandler(error, component, props)

    const amount = getAmount(props)
    return {
        amount,
        onSubmit: executeCallbacks(
            [...beforeSubmit, onSubmit, ...afterSubmit, onPaymentsSuccess],
            props,
        ),
        onAdditionalDetails: executeCallbacks(
            [
                ...beforeAdditionalDetails,
                onAdditionalDetails,
                ...afterAdditionalDetails,
                onPaymentsDetailsSuccess,
            ],
            props,
        ),
        onError: executeErrorCallbacks([...onError, errorHandler], props),
        onPaymentFailed: executeErrorCallbacks([...onError, errorHandler], props),
    }
}

export const onSubmit = async (state, component, actions, props) => {
    try {
        if (!state.isValid) {
            throw new Error('invalid state')
        }
        const basket = props.getBasket ? props.getBasket() : props.basket
        const adyenPaymentService = new AdyenPaymentsService(
            props?.token,
            props?.customerId,
            basket?.basketId,
            props?.site,
        )
        const paymentsResponse = await adyenPaymentService.submitPayment(
            {
                ...state.data,
                origin: state.data.origin || window.location.origin,
                returnUrl: props?.returnUrl || `${window.location.href}/redirect`,
            },
            props.locale,
        )

        return {paymentsResponse: paymentsResponse}
    } catch (err) {
        actions.reject(err)
    }
}

export const onAdditionalDetails = async (state, component, actions, props) => {
    try {
        const basket = props.getBasket ? props.getBasket() : props.basket
        const adyenPaymentsDetailsService = new AdyenPaymentsDetailsService(
            props?.token,
            props?.customerId,
            basket?.basketId,
            props?.site,
        )
        const paymentsDetailsResponse = await adyenPaymentsDetailsService.submitPaymentsDetails(
            state.data,
        )
        return {paymentsDetailsResponse: paymentsDetailsResponse}
    } catch (err) {
        actions.reject(err)
    }
}

export const onErrorHandler = async (error, component, props) => {
    try {
        let newBasketId = error?.newBasketId
        const basket = props.getBasket ? props.getBasket() : props.basket
        if (!newBasketId) {
            const paymentCancelService = new PaymentCancelService(
                props.token,
                props.customerId,
                basket?.basketId,
                props.site,
            )
            const cancelResponse = await paymentCancelService.paymentCancel(props.orderNo)
            if (cancelResponse?.newBasketId) {
                newBasketId = cancelResponse.newBasketId
            }
            // Nothing was cancelled — no abandoned payment existed.
            // This can happen for benign SDK-level errors (e.g. Apple Pay
            // availability check on non-Apple browsers). Don't show error modal.
            if (cancelResponse?.cancelled === false) {
                return {cancelled: false}
            }
        }
        if (props.adyenOrder) {
            props.setAdyenOrder(null)
        }
        if (newBasketId) {
            props.navigate(`/checkout?error=true&newBasketId=${newBasketId}`)
        } else {
            props.navigate(`/checkout?error=true`)
        }
        return {cancelled: true}
    } catch (err) {
        console.error('Error during payment cancellation:', err)
        return {cancelled: false, error: err.message}
    }
}

export const getAmount = ({basket, adyenOrder}) => {
    if (!basket) return null
    if (adyenOrder) {
        return adyenOrder.remainingAmount
    }
    return {
        value: getCurrencyValueForApi(basket.orderTotal, basket.currency),
        currency: basket.currency,
    }
}

const isKlarnaAction = (action) => Boolean(action?.paymentMethodType?.startsWith('klarna'))

const handleAction = (navigate, setAdyenAction, component, response) => {
    const {action, merchantReference} = response
    const actionURI = btoa(JSON.stringify(action))
    const url = `/checkout/confirmation/${merchantReference}?adyenAction=${actionURI}`
    switch (action.type) {
        case 'voucher':
            navigate(url)
            break
        case 'threeDS2':
            setAdyenAction(actionURI)
            break
        default:
            // Push orderNo to URL history so the cancel endpoint can read it on back navigation.
            if (merchantReference) {
                const params = new URLSearchParams(window.location.search)
                params.set('orderNo', merchantReference)
                window.history.pushState(
                    null,
                    '',
                    `${window.location.pathname}?${params.toString()}`,
                )
            }
            // Call handleAction directly on the Dropin element so Adyen Web initialises
            // the Klarna SDK in the correct dropin context (with useKlarnaWidget: true).
            // actions.resolve is also called after this — Adyen Web detects the dropin is
            // already in action state and the second call is effectively a no-op.
            component.handleAction(action)
            break
    }
}

export const onPaymentsSuccess = async (state, component, actions, props, responses) => {
    if (responses?.paymentsResponse?.merchantReference) {
        if (props?.orderNo !== responses?.paymentsResponse?.merchantReference) {
            props?.setOrderNo(responses?.paymentsResponse?.merchantReference)
        }
    }
    if (responses?.paymentsResponse?.order?.orderData) {
        // Only update the order if it's different from the one in the state to prevent re-renders.
        if (props.adyenOrder?.orderData !== responses?.paymentsResponse?.order?.orderData) {
            props?.setAdyenOrder(responses?.paymentsResponse?.order)
        }
    }
    if (responses?.paymentsResponse?.isSuccessful && responses?.paymentsResponse?.isFinal) {
        if (responses?.paymentsResponse?.order) {
            if (responses?.paymentsResponse?.order?.remainingAmount?.value <= 0) {
                props?.navigate(
                    `/checkout/confirmation/${responses?.paymentsResponse?.merchantReference}`,
                )
            }
        } else {
            props?.navigate(
                `/checkout/confirmation/${responses?.paymentsResponse?.merchantReference}`,
            )
        }
    } else if (responses?.paymentsResponse?.action) {
        const {action} = responses.paymentsResponse
        // Voucher and threeDS2 actions are always routed through our own navigation/
        // state handling below. For every other action type, Adyen Web's standard
        // Advanced Flow pattern is to resolve with the action included and let Adyen
        // Web invoke component.handleAction() internally, in sync with the payment
        // method's own lifecycle (SDK popups, await/polling screens, etc.). Manually
        // calling component.handleAction() ourselves and then resolving separately
        // races with that internal invocation — this previously broke PayPal (popup
        // closing immediately) and MBWay (the await/polling screen resolving as
        // "successful" immediately). Klarna is the only known exception that
        // requires the manual call to correctly initialise its SDK in the dropin
        // context.
        const needsManualHandleAction =
            action.type === 'voucher' || action.type === 'threeDS2' || isKlarnaAction(action)
        if (!needsManualHandleAction) {
            actions.resolve(responses.paymentsResponse)
            return
        }
        handleAction(props?.navigate, props?.setAdyenAction, component, responses?.paymentsResponse)
        // Strip 'action' from the resolved data so Adyen Web does not call
        // dropin.handleAction a second time — a second call invalidates the
        // Klarna session and causes ERR302OK.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {action: _omit, ...resolveData} = responses.paymentsResponse
        actions.resolve(resolveData)
        return
    }
    actions.resolve(responses?.paymentsResponse)
}

export const onPaymentsDetailsSuccess = async (state, component, actions, props, responses) => {
    if (responses?.paymentsDetailsResponse?.isSuccessful) {
        props?.navigate(
            `/checkout/confirmation/${responses?.paymentsDetailsResponse?.merchantReference}`,
        )
    } else if (responses?.paymentsDetailsResponse?.action) {
        handleAction(
            props?.navigate,
            props?.setAdyenAction,
            component,
            responses?.paymentsDetailsResponse,
        )
    }
    actions.resolve(responses?.paymentsDetailsResponse)
}
